import { useEffect, useRef, useState } from 'react';
import topApi from '../api/ToPApi';

export interface MerchantOption {
    id: number;
    name: string;
    legalId?: string;
    status?: string;
    parentId?: number | null;
    parent?: { id: number; name: string } | null;    // casa matriz de la sucursal
}

interface Props {
    id?: string;
    value: number;                                   // -1: todos
    onChange: (id: number, merchant?: MerchantOption) => void;
    allLabel?: string;
    pageSize?: number;
    allowAll?: boolean;                              // false: sin la opción "todos" (elegir un comercio)
    params?: Record<string, string>;                 // filtros adicionales de /merchant/scope
    selectedLabel?: string;                          // nombre del comercio ya elegido (al editar)
    invalid?: boolean;
}

const ALL = -1;

/**
 * Selector de comercio con búsqueda en el servidor: muestra los primeros comercios del alcance del usuario
 * (plataforma: todos; banco: los de su banco) y filtra por nombre, identificación o número mientras se escribe.
 * No descarga la lista completa, así funciona igual con pocos o con miles de comercios.
 */
const MerchantPicker = ({ id = 'merchantPicker', value, onChange, allLabel = 'Todos los comercios', pageSize = 20,
    allowAll = true, params, selectedLabel = '', invalid = false }: Props) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<MerchantOption[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const [selectedName, setSelectedName] = useState(selectedLabel);
    const paramsKey = JSON.stringify(params ?? {});

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const requestId = useRef(0);

    // Si el valor vuelve a "todos" desde afuera (por ejemplo, limpiar filtros)
    useEffect(() => {
        if (value === ALL) setSelectedName('');
    }, [value]);

    // Búsqueda 300 ms después de dejar de escribir; se ignoran respuestas viejas
    useEffect(() => {
        if (!open) return;
        const current = ++requestId.current;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const search = new URLSearchParams({ limit: String(pageSize), ...JSON.parse(paramsKey) });
                if (query.trim()) search.set('search', query.trim());
                const resp = await topApi.get<{ merchants: MerchantOption[]; total: number }>(`/merchant/scope?${search.toString()}`);
                if (current !== requestId.current) return;
                setResults(resp.data.merchants ?? []);
                setTotal(resp.data.total ?? 0);
            } catch {
                if (current === requestId.current) { setResults([]); setTotal(0); }
            } finally {
                if (current === requestId.current) setLoading(false);
            }
        }, query ? 300 : 0);
        return () => clearTimeout(timer);
    }, [open, query, pageSize, paramsKey]);

    // Cerrar al hacer clic afuera
    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) close();
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const options: MerchantOption[] = allowAll ? [{ id: ALL, name: allLabel }, ...results] : results;

    const close = () => {
        setOpen(false);
        setQuery('');
        setHighlight(0);
    };

    const select = (option: MerchantOption) => {
        setSelectedName(option.id === ALL ? '' : option.name);
        onChange(option.id, option.id === ALL ? undefined : option);
        close();
        inputRef.current?.blur();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, options.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (options[highlight]) select(options[highlight]); }
        else if (e.key === 'Escape') { close(); }
    };

    const listId = `${id}-list`;

    return (
        <div ref={containerRef} className="position-relative">
            <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-shop" /></span>
                <input
                    ref={inputRef}
                    id={id}
                    className={`form-control${invalid ? ' is-invalid' : ''}`}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    autoComplete="off"
                    placeholder={open ? 'Buscar por nombre, identificación o número...' : allLabel}
                    value={open ? query : selectedName}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => { setQuery(e.target.value); setHighlight(e.target.value && allowAll ? 1 : 0); setOpen(true); }}
                    onKeyDown={onKeyDown}
                />
                {allowAll && value !== ALL && !open && (
                    <button type="button" className="btn btn-outline-secondary" title="Todos los comercios"
                        onClick={() => select({ id: ALL, name: allLabel })}>
                        <i className="bi bi-x-lg" />
                    </button>
                )}
            </div>

            {open && (
                <div className="card shadow position-absolute w-100 mt-1" style={{ zIndex: 1050, minWidth: 280 }}>
                    <ul id={listId} role="listbox" className="list-group list-group-flush" style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {options.map((option, index) => (
                            <li
                                key={option.id}
                                role="option"
                                aria-selected={option.id === value}
                                className={`list-group-item list-group-item-action py-2 ${index === highlight ? 'active' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => setHighlight(index)}
                                onMouseDown={(e) => { e.preventDefault(); select(option); }}
                            >
                                {option.id === ALL ? (
                                    <span className="fst-italic">{option.name}</span>
                                ) : (
                                    <div className="d-flex justify-content-between gap-2">
                                        <span className="text-truncate">
                                            {option.parent && <small className={index === highlight ? '' : 'text-muted'}>{option.parent.name} › </small>}
                                            {option.name}
                                            {option.status === 'INA' && <span className="badge bg-secondary ms-2">Inactivo</span>}
                                        </span>
                                        {option.legalId && <small className={index === highlight ? '' : 'text-muted'}>{option.legalId}</small>}
                                    </div>
                                )}
                            </li>
                        ))}
                        {!loading && results.length === 0 && (
                            <li className="list-group-item text-muted small">No se encontraron comercios.</li>
                        )}
                    </ul>
                    <div className="card-footer bg-white small text-muted py-1 d-flex justify-content-between">
                        {loading
                            ? <span><span className="spinner-border spinner-border-sm me-1" />Buscando...</span>
                            : <span>{total > results.length ? `Mostrando ${results.length} de ${total}. Escriba para filtrar.` : `${total} comercio(s)`}</span>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MerchantPicker;
